import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatDateLong } from 'lib/dateFormat';
import { Button } from 'components/ui/button';
import { StatusBadge } from 'components/ui/status-badge';
import type { ExamScaledReport } from '@examing/types';
import { ExamScoreSummary } from 'modules/standardizedExam/components/report/ExamScoreSummary';
import { ShareScoreButton } from 'modules/standardizedExam/components/share/ShareScoreButton';
import { TOPIC_PASS, topicBreakdown } from './topicBreakdown';

interface ReportAnswer {
  sectionId: string;
  sectionTitle: string;
  isCorrect: boolean;
  itemsCorrect?: number;
  itemsTotal?: number;
  gradingPending?: boolean;
  pointsEarned: number;
  maxPoints: number;
}

export interface ResultReportData {
  testTitle: string;
  percentage: number;
  score: number;
  maxScore: number;
  passed?: boolean | null;
  /** The pass/fail verdict belongs to finished attempts only. */
  status?: 'completed' | 'in_progress' | 'abandoned';
  completedAt: Date | null;
  answers: ReportAnswer[];
  /** IELTS / SAT results show bands or scaled scores instead of a percentage. */
  scaledReport?: ExamScaledReport | null;
}

export interface ResultReportProps {
  data: ResultReportData;
  /** Opens the full per-question review (the existing detail page). */
  onReviewAnswers?: () => void;
  /** Optional top-right actions (e.g. a PDF button). */
  actions?: React.ReactNode;
  /** Re-reads the result while an IELTS Writing or Speaking band is still being checked. */
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}

/**
 * Surface-agnostic report: title and date, the score (or the IELTS/SAT
 * summary), the group-ranking note and the per-topic rows. Group ranking and
 * the pass mark aren't on the payload, so the note says so and the score line
 * shows points only.
 */
export function ResultReport({ data, onReviewAnswers, actions, onRefresh, refreshing, className }: ResultReportProps) {
  const { t } = useTranslation();

  const topics = useMemo(
    () => topicBreakdown(data.answers).map((topic) => ({ ...topic, title: topic.title || t('result.general') })),
    [data.answers, t],
  );

  const showVerdict = data.passed != null && (data.status === undefined || data.status === 'completed');

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-[12.5px] font-medium text-muted-foreground">{t('result.report')}</span>
        {actions}
      </div>
      <h2 className="mt-1.5 font-display text-xl font-bold leading-[1.3] tracking-[-0.01em] text-foreground">
        {data.testTitle}
      </h2>
      {data.completedAt && (
        <p className="mt-1 text-[13.5px] text-muted-foreground">{formatDateLong(data.completedAt)}</p>
      )}

      {data.scaledReport ? (
        <>
          <ExamScoreSummary report={data.scaledReport} className="mt-5" onRefresh={onRefresh} refreshing={refreshing} />
          <ShareScoreButton report={data.scaledReport} testTitle={data.testTitle} className="mt-3 self-start" />
        </>
      ) : (
        <div className="mt-5 flex items-end justify-between gap-3 border-b border-border pb-[18px]">
          <span className="flex flex-col gap-1.5">
            <span className="font-display text-[44px] font-bold leading-none tracking-[-0.03em] tabular-nums text-foreground">
              {Math.round(data.percentage)}%
            </span>
            <span className="text-[13.5px] text-muted-foreground">
              <span className="font-semibold tabular-nums text-foreground">
                {data.score} / {data.maxScore}
              </span>{' '}
              {t('result.pts')}
            </span>
          </span>
          {showVerdict && (
            <StatusBadge tone={data.passed ? 'success' : 'error'} size="md">
              {data.passed ? t('result.passed') : t('result.failed')}
            </StatusBadge>
          )}
        </div>
      )}

      {/* Group ranking — honest gap (no cohort/percentile on the result payload). */}
      <div className="mt-4 flex flex-col gap-1 rounded-sm border border-dashed border-muted-foreground/30 px-3.5 py-3">
        <span className="text-[13.5px] font-semibold text-foreground">{t('result.group_ranking')}</span>
        <span className="text-[13px] leading-[1.45] text-muted-foreground">{t('result.ranking_unavailable')}</span>
      </div>

      {/* By topic — real, derived from graded answers per section */}
      {topics.length > 0 && !data.scaledReport && (
        <>
          <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">{t('result.by_topic')}</h3>
          <ul className="border-b border-border">
            {topics.map((topic) => {
              const pct = topic.percent;
              return (
                <li
                  key={topic.id}
                  className="grid h-10 grid-cols-[minmax(0,1fr)_96px_40px] items-center gap-x-3 border-t border-border"
                >
                  <span className="truncate text-[13.5px] text-foreground">{topic.title}</span>
                  <span aria-hidden="true" className="block h-1.5 overflow-hidden rounded-full bg-muted">
                    <span
                      className={cn('block h-full rounded-full', pct >= TOPIC_PASS ? 'bg-foreground' : 'bg-error-600')}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-right text-[13px] tabular-nums text-foreground/80">
                    {topic.correct}/{topic.total}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {onReviewAnswers && (
        <Button size="lg" onClick={onReviewAnswers} className="mt-[18px] w-full gap-1.5">
          <Eye size={16} aria-hidden="true" />
          {t('result.review_answers')}
        </Button>
      )}
    </div>
  );
}

export default ResultReport;
